/**
 * Suman Sarees - Enterprise Ecommerce Zoom & Pan System
 * - Maximum Ultra-HD source resolution (up to 3840px / master image)
 * - Rock-solid image stability: Click & Drag to pan (no hover glide)
 * - 100% edge-to-edge pan coverage (every pixel, corner, and fabric detail reachable)
 * - Smooth continuous mouse wheel zoom with cursor focal point
 * - Clean UI without mini-map clutter
 * - High-quality image rendering with contrast optimization
 * - Complete floating dock: Prev, Counter, Next, Zoom Out (-), Fit/Scale %, Zoom In (+), Close
 * - Touch support: pinch-to-zoom, swipe, double-tap
 */

class ProductZoomLightbox {
  constructor() {
    this.modal = document.querySelector('.product-zoom-lightbox');
    if (!this.modal) return;

    this.backdrop = this.modal.querySelector('.zoom-lightbox-backdrop');
    this.viewport = this.modal.querySelector('.zoom-lightbox-viewport');
    this.stage = this.modal.querySelector('.zoom-lightbox-stage');
    this.img = this.modal.querySelector('.zoom-lightbox-img');

    // Controls
    this.prevBtn = this.modal.querySelector('.zoom-dock-btn--nav[id^="ZoomPrev"]');
    this.nextBtn = this.modal.querySelector('.zoom-dock-btn--nav[id^="ZoomNext"]');
    this.closeBtn = this.modal.querySelector('.zoom-dock-btn--close');
    this.zoomInBtn = this.modal.querySelector('[id^="ZoomIn"]');
    this.zoomOutBtn = this.modal.querySelector('[id^="ZoomOut"]');
    this.zoomResetBtn = this.modal.querySelector('[id^="ZoomReset"]');
    this.zoomBadge = this.modal.querySelector('.zoom-level-badge');
    this.counterCurrent = this.modal.querySelector('[id^="ZoomCounterCurrent"]');
    this.counterTotal = this.modal.querySelector('[id^="ZoomCounterTotal"]');

    // Tip banner
    this.tip = this.modal.querySelector('[id^="ZoomTip"]');

    const dataEl = this.modal.querySelector('[id^="ProductMediaData"]');
    this.mediaList = dataEl ? JSON.parse(dataEl.textContent) : [];

    // State
    this.currentIndex = 0;
    this.scale = 1.0;
    this.targetScale = 1.0;
    this.minScale = 1.0;
    this.maxScale = 4.0;

    this.translateX = 0;
    this.translateY = 0;
    this.targetTx = 0;
    this.targetTy = 0;

    this.baseWidth = 0;
    this.baseHeight = 0;

    this.isDragging = false;
    this.hasDragged = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragStartTx = 0;
    this.dragStartTy = 0;

    this.rafId = null;

    // Touch pinch state
    this.initialPinchDistance = 0;
    this.initialPinchScale = 1.0;
    this.lastTouchTime = 0;

    this.initEvents();
  }

  initEvents() {
    // Open modal on gallery image click
    document.addEventListener('click', (e) => {
      if (this.isOpen()) return;

      const toggle = e.target.closest('.product__media-toggle, .product__modal-opener, .product__media-item img, .product__media img');
      if (!toggle) return;

      if (toggle.closest('[id^="GalleryThumbnails"]')) return;

      const mediaItem = toggle.closest('.product__media-item');
      if (!mediaItem) return;

      e.preventDefault();
      e.stopPropagation();

      const mediaId = mediaItem.getAttribute('data-media-id');
      let index = 0;
      if (mediaId) {
        const cleanId = mediaId.split('-').pop();
        const found = this.mediaList.findIndex((m) => String(m.id) === String(cleanId));
        if (found !== -1) index = found;
      } else {
        const allItems = Array.from(document.querySelectorAll('.product__media-list .product__media-item'));
        index = allItems.indexOf(mediaItem);
        if (index === -1) index = 0;
      }

      this.open(index);
    }, true);

    // Close on backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Close on close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    // Viewport background click to close when not zoomed
    if (this.viewport) {
      this.viewport.addEventListener('click', (e) => {
        if (this.hasDragged) return;
        if (this.targetScale <= 1.05 && (e.target === this.viewport || e.target === this.stage)) {
          this.close();
        }
      });
    }

    // Navigation buttons
    if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.prev(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.next(); });

    // Dock Zoom buttons
    if (this.zoomInBtn) {
      this.zoomInBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.zoomTo(Math.min(this.maxScale, this.targetScale + 0.65));
      });
    }

    if (this.zoomOutBtn) {
      this.zoomOutBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.zoomTo(Math.max(this.minScale, this.targetScale - 0.65));
      });
    }

    if (this.zoomResetBtn) {
      this.zoomResetBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.targetScale > 1.05) {
          this.zoomTo(1.0);
        } else {
          this.zoomTo(2.25);
        }
      });
    }

    // Image click to toggle/advance zoom
    if (this.img) {
      this.img.addEventListener('click', (e) => {
        e.stopPropagation();
        this.onImageClick(e);
      });
    }

    // Pointer Events for Click & Drag-to-Pan (Strictly on Drag)
    if (this.viewport) {
      this.viewport.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.viewport.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    }

    window.addEventListener('pointermove', (e) => {
      if (!this.isOpen() || !this.isDragging) return;
      this.onPointerMove(e);
    });

    window.addEventListener('pointerup', (e) => {
      if (!this.isOpen()) return;
      this.onPointerUp(e);
    });
    window.addEventListener('pointercancel', (e) => {
      if (!this.isOpen()) return;
      this.onPointerUp(e);
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen()) return;

      if (e.key === 'Escape') this.close();
      else if (e.key === 'ArrowLeft') this.prev();
      else if (e.key === 'ArrowRight') this.next();
      else if (e.key === '+' || e.key === '=') this.zoomTo(Math.min(this.maxScale, this.targetScale + 0.5));
      else if (e.key === '-' || e.key === '_') this.zoomTo(Math.max(this.minScale, this.targetScale - 0.5));
      else if (e.key === '0') this.zoomTo(1.0);
      else if (e.key === ' ' || e.key === 'Enter') this.toggleZoom();
    });

    // Mobile Touch: Pinch & Double-tap
    if (this.viewport) {
      this.viewport.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          this.initialPinchDistance = Math.hypot(dx, dy);
          this.initialPinchScale = this.targetScale;
        } else if (e.touches.length === 1) {
          const now = Date.now();
          if (now - this.lastTouchTime < 300) {
            // Double tap
            e.preventDefault();
            if (this.targetScale > 1.05) {
              this.zoomTo(1.0);
            } else {
              this.zoomTo(2.25, e.touches[0].clientX, e.touches[0].clientY);
            }
          }
          this.lastTouchTime = now;
        }
      }, { passive: false });

      this.viewport.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2 && this.initialPinchDistance > 0) {
          e.preventDefault();
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const currentDistance = Math.hypot(dx, dy);
          const factor = currentDistance / this.initialPinchDistance;
          const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.initialPinchScale * factor));
          const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          this.zoomTo(newScale, midX, midY);
        }
      }, { passive: false });

      this.viewport.addEventListener('touchend', () => {
        this.initialPinchDistance = 0;
      });
    }

    // Window resize recalculates base dimensions
    window.addEventListener('resize', () => {
      if (this.isOpen()) {
        this.calculateBaseDimensions();
        this.clampTranslations();
      }
    });
  }

  isOpen() {
    return this.modal && !this.modal.hidden && this.modal.classList.contains('is-open');
  }

  open(index = 0) {
    if (!this.mediaList.length) return;
    if (this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }
    this.currentIndex = Math.max(0, Math.min(index, this.mediaList.length - 1));
    this.resetZoom(true);

    this.modal.removeAttribute('hidden');
    void this.modal.offsetWidth;
    this.modal.classList.add('is-open');
    document.body.classList.add('zoom-modal-open');

    this.renderImage();

    // Show tip and fade out after 3.5s
    if (this.tip) {
      this.tip.classList.remove('is-hidden');
      clearTimeout(this.tipTimeout);
      this.tipTimeout = setTimeout(() => {
        if (this.tip) this.tip.classList.add('is-hidden');
      }, 3500);
    }

    this.startLoop();
  }

  close() {
    this.modal.classList.remove('is-open');
    this.stopLoop();
    this.resetZoom(true);
    setTimeout(() => {
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('zoom-modal-open');
    }, 250);
  }

  prev() {
    if (!this.mediaList.length) return;
    this.currentIndex = (this.currentIndex - 1 + this.mediaList.length) % this.mediaList.length;
    this.resetZoom(true);
    this.renderImage();
  }

  next() {
    if (!this.mediaList.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.mediaList.length;
    this.resetZoom(true);
    this.renderImage();
  }

  renderImage() {
    const item = this.mediaList[this.currentIndex];
    if (!item) return;

    this.img.style.opacity = '0';
    this.img.decoding = 'async';
    if ('fetchPriority' in this.img) {
      this.img.fetchPriority = 'high';
    }
    this.img.src = item.master_src || item.src;
    this.img.alt = item.alt || '';

    const onLoaded = () => {
      if (!this.img) return;
      this.calculateBaseDimensions();
      this.img.style.opacity = '1';
    };

    this.img.onload = onLoaded;
    if (this.img.complete && this.img.naturalWidth > 0) {
      onLoaded();
    }
    setTimeout(onLoaded, 120);

    if (this.counterCurrent) this.counterCurrent.textContent = this.currentIndex + 1;
    if (this.counterTotal) this.counterTotal.textContent = this.mediaList.length;
  }

  calculateBaseDimensions() {
    if (!this.viewport || !this.img) return;
    const vw = this.viewport.clientWidth || window.innerWidth;
    const vh = this.viewport.clientHeight || window.innerHeight;
    const nw = this.img.naturalWidth || 800;
    const nh = this.img.naturalHeight || 1000;
    const ratio = nw / nh;

    const maxW = vw * 0.88;
    const maxH = vh * 0.85;

    let bw = maxW;
    let bh = bw / ratio;
    if (bh > maxH) {
      bh = maxH;
      bw = bh * ratio;
    }

    this.baseWidth = bw;
    this.baseHeight = bh;
    this.img.style.width = `${Math.round(bw)}px`;
    this.img.style.height = `${Math.round(bh)}px`;

    // Adaptive maxScale to preserve maximum visual fidelity
    const nativeRatio = nw / Math.max(1, bw);
    this.maxScale = Math.max(2.0, Math.min(4.5, Math.max(nativeRatio * 1.25, 2.5)));
  }

  getLimits(scale = this.targetScale) {
    const vw = this.viewport ? this.viewport.clientWidth : window.innerWidth;
    const vh = this.viewport ? this.viewport.clientHeight : window.innerHeight;
    const scaledW = (this.baseWidth || 540) * scale;
    const scaledH = (this.baseHeight || 650) * scale;

    const overflowX = (scaledW - vw) / 2;
    const overflowY = (scaledH - vh) / 2;

    const limitX = Math.max(vw * 0.25, overflowX > 0 ? overflowX + 80 : (vw - scaledW) / 2 + 50);
    const limitY = Math.max(vh * 0.25, overflowY > 0 ? overflowY + 80 : (vh - scaledH) / 2 + 50);

    return { limitX, limitY, vw, vh, scaledW, scaledH };
  }

  clampTranslations() {
    const { limitX, limitY } = this.getLimits(this.targetScale);
    this.targetTx = Math.max(-limitX, Math.min(limitX, this.targetTx));
    this.targetTy = Math.max(-limitY, Math.min(limitY, this.targetTy));
  }

  onPointerDown(e) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target.closest('.zoom-lightbox-dock')) return;

    if (this.targetScale > 1.05) {
      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartTx = this.targetTx;
      this.dragStartTy = this.targetTy;
      this.modal.classList.add('is-dragging');
      if (e.pointerId !== undefined) {
        try { this.viewport.setPointerCapture(e.pointerId); } catch(err) {}
      }
    }
  }

  onPointerMove(e) {
    if (!this.isDragging) return;

    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;
    if (Math.hypot(dx, dy) > 4) this.hasDragged = true;

    const { limitX, limitY } = this.getLimits();
    this.targetTx = Math.max(-limitX, Math.min(limitX, this.dragStartTx + dx));
    this.targetTy = Math.max(-limitY, Math.min(limitY, this.dragStartTy + dy));
  }

  onPointerUp(e) {
    if (this.isDragging) {
      this.isDragging = false;
      this.modal.classList.remove('is-dragging');
      if (e.pointerId !== undefined) {
        try { this.viewport.releasePointerCapture(e.pointerId); } catch(err) {}
      }
    }
  }

  onWheel(e) {
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY < 0 ? 1.25 : 0.8;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.targetScale * factor));
    this.zoomTo(newScale, e.clientX, e.clientY);
  }

  onImageClick(e) {
    if (this.hasDragged) return;

    if (this.targetScale <= 1.05) {
      this.zoomTo(2.5, e.clientX, e.clientY);
    } else if (this.targetScale < 3.2) {
      this.zoomTo(4.0, e.clientX, e.clientY);
    } else {
      this.zoomTo(1.0);
    }
  }

  toggleZoom() {
    if (this.targetScale > 1.05) {
      this.zoomTo(1.0);
    } else {
      this.zoomTo(2.5);
    }
  }

  zoomTo(newScale, focalX, focalY) {
    const oldScale = this.targetScale;
    const clampedScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));
    if (Math.abs(clampedScale - oldScale) < 0.01) return;

    const { vw, vh } = this.getLimits(oldScale);
    const fx = focalX !== undefined ? focalX : vw / 2;
    const fy = focalY !== undefined ? focalY : vh / 2;

    if (clampedScale <= 1.05) {
      this.targetScale = 1.0;
      this.targetTx = 0;
      this.targetTy = 0;
    } else {
      const ratio = clampedScale / oldScale;
      const relX = fx - vw / 2 - this.targetTx;
      const relY = fy - vh / 2 - this.targetTy;
      let nextTx = fx - vw / 2 - relX * ratio;
      let nextTy = fy - vh / 2 - relY * ratio;

      const nextLimits = this.getLimits(clampedScale);
      this.targetTx = Math.max(-nextLimits.limitX, Math.min(nextLimits.limitX, nextTx));
      this.targetTy = Math.max(-nextLimits.limitY, Math.min(nextLimits.limitY, nextTy));
      this.targetScale = clampedScale;
    }

    this.updateZoomClasses();
  }

  updateZoomClasses() {
    if (this.targetScale > 1.05) {
      this.modal.classList.add('is-zoomed');
    } else {
      this.modal.classList.remove('is-zoomed');
    }
  }

  startLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.loop();
  }

  stopLoop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  loop() {
    if (!this.isOpen()) return;

    const lerpFactor = this.isDragging ? 0.45 : 0.18;
    this.translateX += (this.targetTx - this.translateX) * lerpFactor;
    this.translateY += (this.targetTy - this.translateY) * lerpFactor;
    this.scale += (this.targetScale - this.scale) * 0.22;

    if (this.img) {
      this.img.style.transform = `translate3d(${this.translateX.toFixed(2)}px, ${this.translateY.toFixed(2)}px, 0) scale(${this.scale.toFixed(4)})`;
    }

    this.updateBadge();

    this.rafId = requestAnimationFrame(() => this.loop());
  }

  updateBadge() {
    if (this.zoomBadge) {
      const pct = Math.round(this.scale * 100);
      this.zoomBadge.textContent = `${pct}%`;
    }
  }

  resetZoom(immediate = false) {
    this.targetScale = 1.0;
    this.targetTx = 0;
    this.targetTy = 0;
    if (immediate) {
      this.scale = 1.0;
      this.translateX = 0;
      this.translateY = 0;
      if (this.img) {
        this.img.style.transform = 'translate3d(0, 0, 0) scale(1)';
      }
    }
    this.updateZoomClasses();
    if (this.zoomBadge) this.zoomBadge.textContent = '100%';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productZoomLightbox = new ProductZoomLightbox();
  });
} else {
  window.productZoomLightbox = new ProductZoomLightbox();
}


